import { useTranslation } from "@impero-com/common/utils/i18n";
import { Alert, Button, Modal, pendo } from "@impero-com/ui";

import { useGetControlHealthActivitiesActivityIdTimeline } from "api/generated/control-health/control-health";
import { useLastPresent } from "components/modals/useLastPresent";
import { LegacyRoute } from "legacyRoutes";
import { useCurrentUserTimezone } from "utils/currentTimezone";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import { navigateTo } from "utils/legacyNavigation";

import { ControlActivityHealthTag } from "./components/ControlActivityHealthTag";
import { CONTROL_ACTIVITIES_PENDO } from "./consts";
import { ControlActivityTimeline } from "./ControlActivityTimeline";
import { ControlActivityTimelineSkeleton } from "./ControlActivityTimelineSkeleton";
import { ControlActivityTableRow } from "./types";

interface ControlActivityTimelineModalProps {
    activity: ControlActivityTableRow | null;
    onClose: () => void;
}

export function ControlActivityTimelineModal({
    activity,
    onClose,
}: ControlActivityTimelineModalProps) {
    const { t } = useTranslation();
    // Keeps the title, body and footer in place while the modal animates closed.
    const shown = useLastPresent(activity);
    const healthEnabled = useFeatureFlag("controlHealthScoreActivities");

    const footer = (
        <>
            {shown && (
                <Button
                    onClick={() =>
                        navigateTo(
                            `${LegacyRoute.CONTROL_RESULT}/${shown.activityId}`
                        )
                    }
                    {...pendo(
                        CONTROL_ACTIVITIES_PENDO.timelineOpenActivityPage
                    )}
                >
                    {t("ControlActivitiesList_OpenActivityPage")}
                </Button>
            )}

            <Button variant="primary" onClick={onClose}>
                {t("ControlActivitiesList_Close")}
            </Button>
        </>
    );

    return (
        <Modal
            variant="content"
            open={activity !== null}
            onClose={onClose}
            footer={footer}
            title={
                shown?.title ?? t("ControlActivitiesList_TimelineModalTitle")
            }
            titleExtra={
                healthEnabled &&
                shown && (
                    <ControlActivityHealthTag
                        frictionData={shown.frictionData}
                    />
                )
            }
            width={800}
            {...pendo(CONTROL_ACTIVITIES_PENDO.timelineModal)}
        >
            {shown && (
                <ControlActivityTimelineContent activityId={shown.activityId} />
            )}
        </Modal>
    );
}

function ControlActivityTimelineContent({
    activityId,
}: {
    activityId: string;
}) {
    const { t } = useTranslation();
    const timezone = { name: useCurrentUserTimezone() };
    const { data, status } =
        useGetControlHealthActivitiesActivityIdTimeline(activityId);

    if (status === "pending") {
        return <ControlActivityTimelineSkeleton />;
    }

    if (status === "error") {
        return (
            <Alert
                type="error"
                showIcon
                message={t("ControlActivityTimeline_LoadError")}
            />
        );
    }

    const timeline = data.data;

    return (
        <ControlActivityTimeline
            dueDate={timeline.dueDate}
            peopleInvolved={timeline.peopleInvolved}
            rows={timeline.rows}
            completedAt={timeline.completedAt ?? null}
            overdue={timeline.overdue}
            timezone={timezone}
        />
    );
}
